/**
 * "Why TDC and NextOnVera" — why the initiative sits inside Tour De Cure,
 * and why NextOnVera is the flagship platform the Fund exists to back.
 */

export type WhyPoint = {
  title: string;
  body: string;
};

export type NextOnVeraStrategyPhase = {
  id: string;
  index: string;
  title: string;
  functionCopy: string;
  cancerCoverage: string;
};

export const whyTdcNextOnVeraPage = {
  documentTitle: "Why TDC and NextOnVera · NextOnVera",
  eyebrow: "The vehicle",
  title: "Why TDC and NextOnVera",
  lead: "NextOnVera is not a new charity. In close partnership with and fully supported by Tour de Cure because brain cancer needs a grants engine that already works — and it backs NextOnVera because the next options for patients will come from putting evidence easily into the hands of clinical and research teams.",
  heroPanelImage: "brand/nextonvera-logo.png",
  heroPanelImageAlt: "NextOnvera",
  nextonveraImage: "generated-images/nextonvera-hero.png",
  nextonveraImageAlt: "NextOnVera oncology intelligence — evidence-based treatment matching at the bedside.",
  tdc: {
    eyebrow: "The charity",
    heading: "Why Tour De Cure",
    body: [
      "Tour de Cure has spent twenty years assessing cancer research. Independent review of competitive applications, deductible-gift status, and public reporting already sit behind every dollar.",
      "NextOnVera is not a side petition. Backed by Tour de Cure, it benefits from that proven machinery, so early-stage, high-risk work can move without asking donors to trust an untested organisation."
    ],
    points: [
      {
        title: "Proven grants machinery",
        body: "1,328 cancer research projects funded since 2007, and 280 published breakthroughs to date. NextOnVera uses that engine — it does not rebuild it."
      },
      {
        title: "Independent and competitive",
        body: "Every dollar is directed through an independent review process, open to the best ideas from any Australian institution, with transparency on what is funded and what is found."
      },
      {
        title: "Built for trust",
        body: "Registered Australian charity, DGR endorsed, milestone-based reporting. Donors give through a pathway that already exists — not a new one invented for a campaign."
      },
      {
        title: "Speed with discipline",
        body: "The Fund can back ideas too early for NHMRC or MRFF without skipping the scientific assessment donors expect. Fast capital. Same review gates."
      }
    ] satisfies WhyPoint[]
  },
  nextonvera: {
    eyebrow: "The platform",
    heading: "Why NextOnVera",
    body: [
      "NextOnVera is an oncology decision platform that supports clinical decision making and accelerates the pace of research. It is not a research poster. It is a working tool that reads a patient's molecular and clinical picture and puts structured, evidence-based options in front of the tumour board.",
      "Brain cancer is the proving ground — complex, fragmented, time-critical. The architecture is built to extend to other forms of cancer once the methodology is proven."
    ],
    points: [
      {
        title: "Evidence at the bedside",
        body: "Structured reports for oncologists, tumour boards, and researchers — never a black-box replacement for the people who decide."
      },
      {
        title: "Live in clinical review",
        body: "A live case is in clinical review at The Alfred. This is not a slide deck. It is already in the room where care is decided."
      },
      {
        title: "Built for brain cancer first",
        body: "Glioblastoma is where progress has been very limited. NextOnVera is designed around that complexity, then ready to extend pan-cancer."
      },
      {
        title: "Metro intelligence, regional reach",
        body: "Time-poor and regional teams cannot search thousands of disconnected sources before the next treatment decision is made. NextOnVera is built so they have easy access to a richer set of options."
      }
    ] satisfies WhyPoint[]
  },
  breakthroughs: {
    id: "breakthroughs",
    eyebrow: "Tour De Cure record",
    heading: "Breakthroughs",
body: "Every bar is a year of published breakthroughs from Tour De Cure-funded research. The chart covers all 280 dated records from 2010 to 2026. With your support and donations, more breakthroughs can be achieved.",
    source: "Tour De Cure Breakthrough Summary, July 2026",
    xAxisLabel: "Year",
    yAxisLabel: "Breakthroughs"
  },
  strategy: {
    id: "nextonvera-strategy",
    eyebrow: "NextOnVera Strategy",
    heading: "Four phases — function and cancer coverage",
    lead: "NextOnVera is built in four phases. Each one has a job, and each one covers a defined set of cancers. The frames are here; the detail lands as the strategy is locked.",
    phases: [
      {
        id: "phase-1",
        index: "01",
        title: "Phase 1",
        functionCopy: "Function to be confirmed.",
        cancerCoverage: "Cancer coverage to be confirmed."
      },
      {
        id: "phase-2",
        index: "02",
        title: "Phase 2",
        functionCopy: "Function to be confirmed.",
        cancerCoverage: "Cancer coverage to be confirmed."
      },
      {
        id: "phase-3",
        index: "03",
        title: "Phase 3",
        functionCopy: "Function to be confirmed.",
        cancerCoverage: "Cancer coverage to be confirmed."
      },
      {
        id: "phase-4",
        index: "04",
        title: "Phase 4",
        functionCopy: "Function to be confirmed.",
        cancerCoverage: "Cancer coverage to be confirmed."
      }
    ] satisfies NextOnVeraStrategyPhase[]
  },
  cta: {
    eyebrow: "Next step",
    heading: "See the platform, or back the work",
    body: "Read how NextOnVera works, or give to the Brain Cancer Fund — any amount, any donor — so early science and this platform both keep moving.",
    primary: { label: "Explore NextOnVera", href: "/nextonvera", variant: "default" as const },
    secondary: { label: "The Fund", href: "/the-fund", variant: "outline" as const },
    donateLabel: "Donate with Tour De Cure"
  }
} as const;
